/* global CLIENT_STORAGE_SCHEMA_VERSION */
import CustomError from './CustomError';
import CookieStorage from './CookieStorage';
import LocalStorage from './LocalStorage';
import Utilities from './Utilities';
import { shortNameMapping } from '../constants';

const STORE_FAILURE_MESSAGE = 'Persistent storage is deactivated or not supported by current browser.';

/**
 * Reads a key from the configured stores
 *
 * @param {*[]} stores - Current available storage implementations
 * @param {String} key - Key for lookup item in store
 * @return {Promise<[*]>}
 */
const readFromStores = (stores, key) => new Promise((resolve, reject) => {
  const results = [];
  try {
    for (let i = 0, iLen = stores.length; i < iLen; i += 1) {
      const storage = stores[i];
      const result = storage.impl.getItem(key);
      results.push({
        storage: {
          name: storage.name,
          label: storage.label,
        },
        result: result.value,
      });
    }
    resolve(results);
  } catch (error) {
    reject(error);
  }
});
/**
 * Saves a key with a given value to the store
 *
 * @param {*[]} stores - Current available storage implementations
 * @param {String} key - Key of the items to save in store
 * @param {[*]} values - The values to of the items to save
 * @return {Promise<void>}
 */
const saveToStores = (stores, key, values) => new Promise((resolve, reject) => {
  try {
    for (let i = 0, iLen = stores.length; i < iLen; i += 1) {
      const storage = stores[i];
      storage.impl.setItem(key, values[i]);
    }
    resolve();
  } catch (error) {
    reject(error);
  }
});

/**
 * Creates a collection of storage engines supported by current browser
 *
 * @return {[]} A collection fo supported storage engines
 */
const createStores = (store) => {
  const { sensor } = store;
  const {
    basicSettings,
    logger,
    state,
  } = sensor;
  const cookieStorage = new CookieStorage(window.location.hostname);
  const localStorage = new LocalStorage();
  const stores = [];
  const regex = new RegExp(`^(${basicSettings.storage.data.key}|${basicSettings.storage.data.checksum})_[0-9a-f]{40}`);
  // Check if persistence is activated, cookie storage is available and cookies are not deactivated by the user agent.
  if (state.persistence === true && cookieStorage.available === true && state.globalParams.localData !== 'nocookie') {
    const { count, size } = cookieStorage.stats;
    // We have to check if current storage space for cookies is limited
    if (size > basicSettings.maxCookieStorageSize) {
      // Log information about quota reaching
      logger.debug('Cookie storage quota reached limit', basicSettings.maxCookieStorageSize, 'with currently', count, 'elements and a overall size of', size, '.');
      // Create a regex to find all acsam cookies stored in current browser
      // Remove all acsam cookies (data and checksum) by the created regular expression
      const results = cookieStorage.removeItems(regex);
      logger.debug('Cookie storage quota reached limit', results.length, 'cookies are removed from local cookie storage.');
      // Create a script error to inform about cookie quota limit reaching
      state.scriptErrors.push(new CustomError('E7', 'Cookie storage quota is on limit. IMA cookies are removed from storage!'));
    } else {
      stores.push({
        label: 'cookie storage',
        name: 'cookie',
        impl: cookieStorage,
      });
    }
  }
  // Check for 'nocookie' option
  if (state.persistence === true && cookieStorage.available === true && state.globalParams.localData === 'nocookie') {
    // Remove all acsam cookies (data and checksum) by the created regular expression when cookie storage is deactivated by the sensor options.
    const results = cookieStorage.removeItems(regex);
    logger.debug('Cookie storage was deactivated by sensor options. ', results.length, 'cookies are removed from local cookie storage.');
    // Create a script error to inform about cookie deactivation
    state.scriptErrors.push(new CustomError('E8', 'Cookie storage was deactivated by the sensor options! Present cookies were removed!'));
  }
  // Check if persistence is activated and local storage is available
  if (localStorage.available === true && state.persistence === true) {
    stores.push({
      label: 'local storage',
      name: 'localStorage',
      impl: localStorage,
    });
  }
  return stores;
};

export default class Store {
  /**
   * Initializes a new store instance
   *
   * @param {Sensor} sensor - Sensor instance
   */
  constructor(sensor) {
    this.sensor = sensor;
  }

  /**
   * Saves the current data store in the sensor instance persistent to local storage
   *
   * @return {Promise<void>} Returns a promise which resolves always
   * @public
   */
  save() {
    return new Promise((resolve) => {
      const {
        basicSettings,
        clsData,
        logger,
        state,
        crypto,
      } = this.sensor;
      const stores = createStores(this);
      if (stores.length > 0) {
        try {
          const encryptionPromises = [];
          // Update creation time of CLS data
          clsData.data.savedAt = new Date().getTime();
          for (let i = 0, iLen = stores.length; i < iLen; i += 1) {
            const storage = stores[i];
            let data = { ...clsData.data };
            // Add sensor version to storage object
            data.version = CLIENT_STORAGE_SCHEMA_VERSION;
            logger.debug(`'${storage.label}' data before processing: `, data);
            // Process cookie data differently than for local storage to minimize the size of the first party cookie
            if (storage.name === 'cookie') {
              // Remove specific keys with potentially large data sets from cookie data:
              // Delete code data
              delete data.code;
              // Delete profile data
              delete data.profile;
              // Delete url cache
              delete data.urlCache;
              // Abbreviate all keys for the cookie data
              data = Utilities.transformKeys(data, shortNameMapping);
              logger.debug(`'${storage.label}' data after abbreviation and clearing up: `, data);
            }
            // Serialize data to JSON
            data = JSON.stringify(data);
            logger.debug(`'${storage.label}' data after JSON serialization: `, data);
            if (storage.name === 'cookie') {
              // Compress cookie data with LZW algorithm
              data = Utilities.compress(data);
              logger.debug(`'${storage.label}' data after LZW compression: `, data);
            }
            encryptionPromises.push(crypto.encryptAesCbc(data, basicSettings.password));
          }
          Promise.all(encryptionPromises)
            .then((encryptionResults) => {
              const checksumAndStorePromises = [];
              for (let i = 0, iLen = stores.length; i < iLen; i += 1) {
                checksumAndStorePromises.push(crypto.hash(encryptionResults[i]));
              }
              checksumAndStorePromises.unshift(saveToStores(stores, `${basicSettings.storage.data.key}_${state.globalParams.siteHash}`, encryptionResults));
              return Promise.all(checksumAndStorePromises);
            })
            .then((hashAndSaveResults) => {
              const checksums = [];
              for (let i = 0, iLen = stores.length; i < iLen; i += 1) {
                const storage = stores[i];
                const checksum = hashAndSaveResults[i + 1];
                logger.debug(`Save '${storage.label}' data checksum: `, checksum);
                checksums.push(checksum);
              }
              // Store encrypted data and checksum to stores
              return saveToStores(stores, `${basicSettings.storage.data.checksum}_${state.globalParams.siteHash}`, checksums);
            })
            .then(() => resolve())
            .catch((error) => {
              state.scriptErrors.push(new CustomError('E6', error.message));
              resolve();
            });
        } catch (error) {
          state.scriptErrors.push(new CustomError('E5', error.message));
          resolve();
        }
      } else {
        state.scriptErrors.push(new CustomError('E0', STORE_FAILURE_MESSAGE));
        // Persistent storage is not available so we have to store this fact into the state to react to it properly
        state.persistence = false;
        resolve();
      }
    });
  }

  /**
   * Reads the sensor data from local storage and stores in the current data property of the sensor instance
   *
   * @return {Promise.<void>} Returns a promise which resolves always
   * @public
   */
  read() {
    return new Promise((resolve) => {
      const {
        basicSettings,
        crypto,
        logger,
        state,
      } = this.sensor;
      const stores = createStores(this);
      if (stores.length > 0) {
        const readPromises = [
          readFromStores(stores, `${basicSettings.storage.data.key}_${state.globalParams.siteHash}`),
          readFromStores(stores, `${basicSettings.storage.data.checksum}_${state.globalParams.siteHash}`),
        ];
        Promise.all(readPromises)
          .then(([readData, readChecksums]) => {
            // Filter undefined storage data out of result.
            const data = readData.filter((item) => {
              // On Edge the cookie store results with '0', in Chrome and FF it results with
              if (item.result !== undefined) {
                return true;
              }
              // Create a message to indicate that no data was found in specific browser storage
              const message = `No data found in '${item.storage.label}'.`;
              state.scriptErrors.push(new CustomError('E1', message));
              logger.debug(message);
              return false;
            });
            // Filter undefined checksums out of result
            const checksums = readChecksums.filter(checksum => checksum.result !== undefined);
            return { filteredData: data, filteredChecksums: checksums };
          })
          .then(({ filteredData, filteredChecksums }) => {
            const hashPromises = [];
            for (let i = 0, iLen = filteredData.length; i < iLen; i += 1) {
              const { result } = filteredData[i];
              // Push a checksum creation task to a promise map
              hashPromises.push(crypto.hash(result));
            }
            // Execute checksum creation promise map
            return Promise
              .all(hashPromises)
              .then(createdChecksums => ({ filteredData, filteredChecksums, createdChecksums }));
          })
          .then(({ filteredData, filteredChecksums, createdChecksums }) => {
            const decryptionPromises = [];
            for (let i = 0, iLen = createdChecksums.length; i < iLen; i += 1) {
              const { storage } = filteredData[i];
              // Check checksums
              if (filteredChecksums[i].result === createdChecksums[i]) {
                const { result } = filteredData[i];
                logger.debug(`Read encrypted '${storage.label}' data: `, result);
                decryptionPromises.push(crypto.decryptAesCbc(result, basicSettings.password));
              } else {
                const message = `Checksum mismatch while reading data from '${storage.label}'.`;
                state.scriptErrors.push(new CustomError('E2', message));
                logger.debug(message);
              }
            }
            return Promise
              .all(decryptionPromises)
              .then(decryptionResults => decryptionResults
                .map((result, index) => ({ result, storage: filteredData[index].storage })));
          })
          .then((decryptionResults) => {
            const serializedData = [];
            if (decryptionResults.length > 0) {
              // We have to serialize the JSON to JS Objects by iterating over all decryption results
              for (let i = 0, iLen = decryptionResults.length; i < iLen; i += 1) {
                const { storage } = decryptionResults[i];
                let { result } = decryptionResults[i];
                try {
                  // Process the cookie data differently than for the local storage, because the data is compress with LZW algorithm.
                  if (storage.name === 'cookie') {
                    // Decompress the results from the cookie storage
                    result = Utilities.decompress(result);
                  }
                  // Try to parse decryption result
                  let parsedData = JSON.parse(result);
                  // Process the cookie data differently than for the local storage, because the keys where abbreviated before data was stored as
                  // cookie.
                  if (storage.name === 'cookie') {
                    // Flip short name mapping
                    const mapping = Utilities.flipKeyValues(shortNameMapping);
                    // Transform keys of parsed data to the long form
                    parsedData = Utilities.transformKeys(parsedData, mapping);
                  }
                  // Push data to an collection for later processing
                  serializedData.push({ ...parsedData, storage: { type: storage.name } });
                } catch (error) {
                  // Catch serialization errors separately
                  const message = `Data from store '${storage.label}' could not be parsed: ${error.message}`;
                  state.scriptErrors.push(new CustomError('E3', message));
                  logger.debug(message);
                }
              }
              if (serializedData.length > 1) {
                // Merge the two data streams with the current cls data by checking which is younger
                if (serializedData[0].savedAt <= serializedData[1].savedAt) {
                  // Cookie data is older or equal than the local storage data so we take the local storage data. This is the default behaviour.
                  this.sensor.clsData.data = { ...this.sensor.clsData.data, ...serializedData[1] };
                } else {
                  // The local storage data is older than the cookie data so we take the cookie data but we merge it with the local storage
                  // data because we need the profile, code, category and the url cache which are only stored in the local storage data.
                  this.sensor.clsData.data = { ...this.sensor.clsData.data, ...serializedData[1], ...serializedData[0] };
                }
              } else {
                // There are only one storage engine available so we use the first serialized data item and merge it with current cls data
                this.sensor.clsData.data = { ...this.sensor.clsData.data, ...serializedData[0] };
              }
            }
            resolve();
          })
          .catch((error) => {
            const message = `Unknown error while reading data from browser storage: ${error.message}`;
            state.scriptErrors.push(new CustomError('E4', message));
            logger.debug(error.message);
            resolve();
          });
      } else {
        state.scriptErrors.push(new CustomError('E0', STORE_FAILURE_MESSAGE));
        // Persistent storage is not available so we have to store this fact into the state to react to it properly
        state.persistence = false;
        resolve();
      }
    });
  }

  /**
   * Reads the sensor configuration from local storage
   */
  readConfig(siteHash) {
    return new Promise((resolve, reject) => {
      try {
        const {
          basicSettings,
          crypto,
          logger,
          state,
        } = this.sensor;
        const stores = createStores(this);
        if (stores.length > 0) {
          const storage = stores.find(item => item.name === 'localStorage');
          if (storage) {
            const readPromises = [
              readFromStores([storage], `${basicSettings.storage.config.key}_${siteHash}`),
              readFromStores([storage], `${basicSettings.storage.config.checksum}_${siteHash}`),
            ];
            Promise.all(readPromises)
              .then(([readConfigs, readChecksums]) => {
                const readConfig = readConfigs[0];
                const readChecksum = readChecksums[0];
                if (!readConfig.result) {
                  // Create a message to indicate that no configuration was found in specific browser storage
                  const message = `No configuration found in '${storage.label}'.`;
                  const error = new CustomError('E1', message);
                  state.scriptErrors.push(error);
                  logger.debug(message);
                  return Promise.reject(error);
                }
                if (!readChecksum.result) {
                  // Create a message to indicate that no checksum was found in specific browser storage
                  const message = `No checksum found for config in '${storage.label}'.`;
                  const error = new CustomError('E1', message);
                  state.scriptErrors.push(error);
                  logger.debug(message);
                  return Promise.reject(error);
                }
                return { encryptedConfig: readConfig.result, checksum: readChecksum.result };
              })
              .then(({ encryptedConfig, checksum }) => crypto
                .decryptAesCbc(encryptedConfig, basicSettings.password)
                .then(decryptionResult => ({ decryptionResult, checksum })))
              .then(({ decryptionResult, checksum }) => crypto
                .hash(decryptionResult)
                .then(checksumToCheck => ({ decryptionResult, checksum, checksumToCheck })))
              .then(({ decryptionResult, checksum, checksumToCheck }) => {
                if (checksum !== checksumToCheck) {
                  // Create a message to indicate that no checksum was found in specific browser storage
                  const message = `Checksum mismatch while reading config from '${storage.label}'.`;
                  const error = new CustomError('E2', message);
                  state.scriptErrors.push(error);
                  logger.debug(message);
                  return Promise.reject(error);
                }
                return decryptionResult;
              })
              .then((decryptionResult) => {
                if (!decryptionResult) {
                  // Create a message to indicate that decryption was unsuccessful
                  const message = `Error while decrypting config from '${storage.label}'.`;
                  const error1 = new CustomError('E3', message);
                  state.scriptErrors.push(error1);
                  logger.debug(message);
                  return Promise.reject(error1);
                }
                try {
                  return JSON.parse(decryptionResult);
                } catch (error) {
                  // Create a message to indicate that decryption was unsuccessful
                  const message = `Config from store '${storage.label}' could not be parsed: ${error.message}`;
                  const error2 = new CustomError('E3', message);
                  state.scriptErrors.push(error2);
                  logger.debug(message);
                  return Promise.reject(error2);
                }
              })
              .then((parsedData) => {
                const now = new Date().getTime();
                const ttl = now - parsedData.savedAt;
                if (ttl > basicSettings.storage.config.ttl) {
                  // Create a message to indicate that no checksum was found in specific browser storage
                  const message = `Config found in '${storage.label} is too old. Should be fresh fetched from relay'.`;
                  const error = new CustomError('E3', message);
                  state.scriptErrors.push(error);
                  logger.debug(message);
                  return Promise.reject(error);
                }
                return parsedData.config;
              })
              .then(result => resolve(result))
              .catch((error) => {
                reject(error);
              });
          } else {
            const message = 'Local storage not supported.';
            const error = new CustomError('E0', message);
            state.scriptErrors.push(error);
            logger.debug(message);
            throw error;
          }
        } else {
          const message = 'Storage persistence not supported.';
          const error = new CustomError('E0', message);
          state.scriptErrors.push(error);
          logger.debug(message);
          throw error;
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Saves the sensor config retrieved from the relay to the local storage
   *
   * @param {Object} config - Sensor configuration
   * @returns {Promise} Resolves with true or rejects with error
   */
  saveConfig(config) {
    return new Promise((resolve, reject) => {
      try {
        const {
          basicSettings,
          logger,
          state,
          crypto,
        } = this.sensor;
        const stores = createStores(this);
        if (stores.length > 0) {
          const storage = stores.find(item => item.name === 'localStorage');
          if (storage) {
            const data = {
              savedAt: new Date().getTime(),
              config,
            };
            const text = JSON.stringify(data);
            crypto
              .hash(text)
              .then(checksum => crypto.encryptAesCbc(text, basicSettings.password)
                .then(encryptionResult => ({ encryptionResult, checksum })))
              .then(({ encryptionResult, checksum }) => {
                const promises = [
                  saveToStores([storage], `${basicSettings.storage.config.checksum}_${state.globalParams.siteHash}`, [checksum]),
                  saveToStores([storage], `${basicSettings.storage.config.key}_${state.globalParams.siteHash}`, [encryptionResult]),
                ];
                return Promise.all(promises);
              })
              .then(() => resolve(config))
              .catch(error => reject(error));
          } else {
            const message = 'Local storage not supported.';
            const error = new CustomError('E0', message);
            state.scriptErrors.push(error);
            logger.debug(message);
            throw error;
          }
        } else {
          const message = 'Storage persistence not supported.';
          const error = new CustomError('E0', message);
          state.scriptErrors.push(error);
          logger.debug(message);
          throw error;
        }
      } catch (error) {
        reject(error);
      }
    });
  }
}
